"use client";

import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import useEmblaCarousel from "embla-carousel-react";
import { EmblaCarouselType } from "embla-carousel";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type ProductGallerySlide = {
  type: "image" | "video";
  src: string;
  thumb: string;
  alt: string;
};

type Props = {
  slides: ProductGallerySlide[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
};

const fontFamily = '"Yekan Bakh FaNum", Tahoma, sans-serif';
const accent = "#E7A66B";
const springSoft = { type: "spring" as const, stiffness: 320, damping: 30, mass: 0.85 };
const easeOut = [0.22, 1, 0.36, 1] as const;

/** soft day/night by local clock — night 19:00–07:00 */
function isNightHours(date = new Date()) {
  const hour = date.getHours();
  return hour < 7 || hour >= 19;
}

function msUntilDayNightFlip(date = new Date()) {
  const next = new Date(date);
  const hour = date.getHours();
  if (hour < 7) next.setHours(7, 0, 0, 0);
  else if (hour < 19) next.setHours(19, 0, 0, 0);
  else {
    next.setDate(next.getDate() + 1);
    next.setHours(7, 0, 0, 0);
  }
  return Math.max(0, next.getTime() - date.getTime());
}

const galleryTheme = {
  night: {
    bg: "rgba(8, 8, 10, 0.97)",
    fg: "#fff",
    muted: "rgba(255,255,255,0.68)",
    chrome: "rgba(8, 8, 10, 0.55)",
    dock: "rgba(255,255,255,0.04)",
    counterBg: "rgba(255,255,255,0.08)",
    counterRing: "rgba(255,255,255,0.1)",
    controlFg: "#fff",
    controlBg: "rgba(255,255,255,0.1)",
    controlBorder: "rgba(255,255,255,0.1)",
    controlHover: "rgba(255,255,255,0.18)",
    thumbBg: "rgba(255,255,255,0.06)",
    thumbRing: "rgba(255,255,255,0.12)",
    thumbIdle: 0.42,
    progressTrack: "rgba(255,255,255,0.08)",
  },
  day: {
    bg: "rgba(242, 241, 238, 0.98)",
    fg: "#1c1b19",
    muted: "rgba(28,27,25,0.58)",
    chrome: "rgba(242, 241, 238, 0.72)",
    dock: "rgba(28,27,25,0.03)",
    counterBg: "rgba(28,27,25,0.05)",
    counterRing: "rgba(28,27,25,0.08)",
    controlFg: "#1c1b19",
    controlBg: "rgba(28,27,25,0.05)",
    controlBorder: "rgba(28,27,25,0.1)",
    controlHover: "rgba(28,27,25,0.1)",
    thumbBg: "rgba(28,27,25,0.04)",
    thumbRing: "rgba(28,27,25,0.1)",
    thumbIdle: 0.5,
    progressTrack: "rgba(28,27,25,0.08)",
  },
} as const;

type GalleryImageProps = {
  src: string;
  thumb: string;
  alt: string;
  active: boolean;
  load: boolean;
  reduceMotion: boolean;
  spinnerColor: string;
  onError: () => void;
};

// shortcut: match ProductImagesView main image sizes/quality so /_next/image cache hits
const pagePreviewSizes = "(max-width: 899px) 72vw, (max-width: 1280px) 42vw, 36vw";
const pagePreviewQuality = 80;

function GalleryImage({
  src,
  thumb,
  alt,
  active,
  load,
  reduceMotion,
  spinnerColor,
  onError,
}: GalleryImageProps) {
  const [loaded, setLoaded] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setPreviewReady(false);
    setShowSpinner(false);
  }, [src]);

  useEffect(() => {
    if (!load || loaded || previewReady) {
      setShowSpinner(false);
      return;
    }
    const timer = window.setTimeout(() => setShowSpinner(true), 160);
    return () => window.clearTimeout(timer);
  }, [load, loaded, previewReady]);

  return (
    <div
      onClick={(event) => event.stopPropagation()}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        maxWidth: "100%",
        maxHeight: "100%",
        userSelect: "none",
        WebkitUserSelect: "none",
        // none: browser must not treat vertical pan as page zoom/overscroll
        touchAction: "none",
      }}
    >
      {thumb ? (
        <div
          aria-hidden
          style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0 }}
        >
          <Image
            src={thumb}
            alt=""
            fill
            sizes={pagePreviewSizes}
            quality={pagePreviewQuality}
            draggable={false}
            {...(active ? { priority: true as const } : { loading: "eager" as const })}
            onLoadingComplete={() => setPreviewReady(true)}
            style={{ objectFit: "contain" }}
          />
        </div>
      ) : null}

      {showSpinner ? (
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          <CircularProgress size={34} thickness={3.2} sx={{ color: spinnerColor }} />
        </div>
      ) : null}

      {load ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            // only fade HQ in over solid preview — never fade preview out
            opacity: loaded ? 1 : 0,
            transition: reduceMotion || !loaded ? undefined : "opacity 0.2s ease",
          }}
        >
          <Image
            src={src}
            alt={alt}
            fill
            sizes="100vw"
            quality={85}
            {...(active ? { priority: true as const } : { loading: "eager" as const })}
            draggable={false}
            onLoadingComplete={() => setLoaded(true)}
            onError={onError}
            style={{ objectFit: "contain" }}
          />
        </div>
      ) : null}
    </div>
  );
}

export default function ProductGallery({ slides, index, onIndexChange, onClose }: Props) {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(true);
  const [night, setNight] = useState(() => isNightHours());
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const theme = night ? galleryTheme.night : galleryTheme.day;
  const controlSx = {
    width: 44,
    height: 44,
    color: theme.controlFg,
    bgcolor: theme.controlBg,
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    border: `1px solid ${theme.controlBorder}`,
    transition: "background-color 160ms ease, transform 160ms ease, border-color 160ms ease",
    "&:hover": { bgcolor: theme.controlHover, transform: "scale(1.05)" },
    "&:active": { transform: "scale(0.96)" },
  } as const;
  const [loadedSet, setLoadedSet] = useState(() => new Set<number>([index]));
  const [dismissY, setDismissY] = useState(0);
  const [dismissDragging, setDismissDragging] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);
  const didInitScroll = useRef(false);
  const dismissStart = useRef<{
    id: number;
    x: number;
    y: number;
    locked: "x" | "y" | null;
  } | null>(null);
  const suppressCloseClick = useRef(false);
  const indexRef = useRef(index);
  const dismissDragRef = useRef(false);
  const dismissYRef = useRef(0);
  const pointersDown = useRef(0);
  indexRef.current = index;
  dismissYRef.current = dismissY;
  const reduceMotion = useReducedMotion() ?? false;

  const requestClose = useCallback(() => {
    if (reduceMotion) {
      onClose();
      return;
    }
    setOpen(false);
  }, [onClose, reduceMotion]);

  const closeFromEmpty = useCallback(() => {
    if (suppressCloseClick.current) {
      suppressCloseClick.current = false;
      return;
    }
    requestClose();
  }, [requestClose]);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    direction: "rtl",
    align: "center",
    containScroll: false,
    duration: reduceMotion ? 0 : 28,
    // only our pull-down (or multi-touch) should block Embla
    watchDrag: () => !dismissDragRef.current && pointersDown.current < 2,
  });

  const [thumbsRef, thumbsApi] = useEmblaCarousel({
    containScroll: "keepSnaps",
    dragFree: true,
    direction: "rtl",
    align: "center",
  });

  const count = slides.length;
  const slide = slides[index];
  const isImage = slide?.type === "image";
  const canPrev = index > 0;
  const canNext = index < count - 1;
  const centerThumbs = count <= 8;

  const cancelDismissDrag = useCallback(() => {
    dismissStart.current = null;
    dismissDragRef.current = false;
    setDismissDragging(false);
    setDismissY(0);
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setNight(isNightHours());
    let timer: ReturnType<typeof setTimeout>;
    const arm = () => {
      timer = setTimeout(() => {
        setNight(isNightHours());
        arm();
      }, msUntilDayNightFlip() + 50);
    };
    arm();
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!mounted || !open) return;
    dialogRef.current?.focus();
  }, [mounted, open]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    setFailedSrc(null);
    cancelDismissDrag();
  }, [index, cancelDismissDrag]);

  useEffect(() => {
    setLoadedSet((prev) => {
      const next = new Set<number>();
      for (let i = 0; i < count; i++) {
        if (Math.abs(i - index) <= 1 || (prev.has(i) && Math.abs(i - index) <= 2)) {
          next.add(i);
        }
      }
      next.add(index);
      return next;
    });
  }, [index, count]);

  useEffect(() => {
    if (!emblaApi) return;
    if (emblaApi.selectedScrollSnap() === index) {
      didInitScroll.current = true;
      syncing.current = false;
      return;
    }
    const jump = reduceMotion || !didInitScroll.current;
    didInitScroll.current = true;
    syncing.current = true;
    emblaApi.scrollTo(index, jump);
    const clear = () => {
      syncing.current = false;
    };
    if (jump) {
      queueMicrotask(clear);
      return;
    }
    emblaApi.on("settle", clear);
    return () => {
      emblaApi.off("settle", clear);
      syncing.current = false;
    };
  }, [emblaApi, index, reduceMotion]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = (api: EmblaCarouselType) => {
      if (syncing.current) return;
      const next = api.selectedScrollSnap();
      if (next !== indexRef.current) onIndexChange(next);
    };
    const onScroll = () => {
      suppressCloseClick.current = true;
    };
    emblaApi.on("select", onSelect);
    emblaApi.on("scroll", onScroll);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("scroll", onScroll);
    };
  }, [emblaApi, onIndexChange]);

  useEffect(() => {
    thumbsApi?.scrollTo(index);
  }, [thumbsApi, index]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        requestClose();
        return;
      }
      if (event.target instanceof HTMLElement && event.target.closest("video")) return;
      if (event.key === "ArrowRight") {
        event.preventDefault();
        if (canPrev) onIndexChange(index - 1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        if (canNext) onIndexChange(index + 1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canNext, canPrev, index, onIndexChange, requestClose]);

  const goPrev = () => {
    if (!canPrev) return;
    onIndexChange(index - 1);
  };

  const goNext = () => {
    if (!canNext) return;
    onIndexChange(index + 1);
  };

  const onDismissPointerDown = (event: React.PointerEvent) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    pointersDown.current += 1;
    // two+ fingers → leave pinch to the OS; abort dismiss
    if (pointersDown.current >= 2) {
      cancelDismissDrag();
      return;
    }
    dismissStart.current = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      locked: null,
    };
  };

  const onDismissPointerMove = (event: React.PointerEvent) => {
    if (pointersDown.current >= 2) {
      cancelDismissDrag();
      return;
    }
    const start = dismissStart.current;
    if (!start || start.id !== event.pointerId) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;

    if (!start.locked) {
      if (Math.hypot(dx, dy) < 12) return;
      // only downward pull dismisses — upward / horizontal stay with Embla
      if (Math.abs(dy) > Math.abs(dx) * 1.2 && dy > 0) {
        start.locked = "y";
        dismissDragRef.current = true;
        setDismissDragging(true);
        suppressCloseClick.current = true;
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
          /* ignore */
        }
      } else {
        start.locked = "x";
      }
    }

    if (start.locked !== "y") return;
    event.preventDefault();
    event.stopPropagation();
    const nextY = Math.max(0, dy);
    dismissYRef.current = nextY;
    setDismissY(nextY);
  };

  const onDismissPointerUp = (event: React.PointerEvent) => {
    pointersDown.current = Math.max(0, pointersDown.current - 1);

    const start = dismissStart.current;
    if (!start || start.id !== event.pointerId) return;

    const y = dismissYRef.current;
    const wasVertical = start.locked === "y";
    dismissStart.current = null;
    dismissDragRef.current = false;
    setDismissDragging(false);

    if (wasVertical && y > 110) {
      requestClose();
      return;
    }
    setDismissY(0);
  };

  if (!mounted || !slide) return null;

  const counter = `${(index + 1).toLocaleString("fa-IR")} / ${count.toLocaleString("fa-IR")}`;
  const progress = count > 0 ? (index + 1) / count : 0;
  const chromeTransition = reduceMotion ? { duration: 0 } : { duration: 0.32, ease: easeOut };

  return createPortal(
    <AnimatePresence onExitComplete={onClose}>
      {open ? (
        <Box
          key="product-gallery"
          component={motion.div}
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label="گالری تصاویر محصول"
          tabIndex={-1}
          dir="rtl"
          lang="fa"
          initial={reduceMotion ? false : { opacity: 0, y: 36, scale: 0.98, borderRadius: 0, filter: "blur(0px)" }}
          animate={{
            opacity: reduceMotion ? 1 : 1 - Math.min(dismissY / 420, 0.5),
            y: dismissY,
            scale: reduceMotion ? 1 : 1 - Math.min(dismissY / 2200, 0.035),
            borderRadius: reduceMotion ? 0 : Math.min(dismissY / 7, 32),
            filter: reduceMotion ? "blur(0px)" : `blur(${Math.min(dismissY / 26, 14)}px)`,
          }}
          exit={
            reduceMotion
              ? undefined
              : {
                  opacity: 0,
                  y: Math.max(dismissY, 64),
                  scale: 0.975,
                  borderRadius: Math.max(Math.min(dismissY / 7, 32), 24),
                  filter: `blur(${Math.max(Math.min(dismissY / 26, 14), 10)}px)`,
                }
          }
          transition={
            dismissDragging
              ? { duration: 0 }
              : reduceMotion
                ? { duration: 0 }
                : open
                  ? { type: "spring", stiffness: 320, damping: 34, mass: 0.9 }
                  : { duration: 0.34, ease: easeOut }
          }
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            bgcolor: theme.bg,
            color: theme.fg,
            fontFamily,
            outline: "none",
            overflow: "hidden",
            willChange: "transform, opacity, filter, border-radius",
          }}
        >
          <Box
            component={motion.div}
            initial={reduceMotion ? false : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={chromeTransition}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 4,
              pt: "env(safe-area-inset-top)",
              background: `linear-gradient(to bottom, ${theme.bg} 0%, ${theme.chrome} 70%, transparent)`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                px: { xs: 1.25, md: 2.5 },
                py: 1,
                minHeight: { xs: 56, md: 60 },
              }}
            >
              <IconButton aria-label="بستن" onClick={requestClose} sx={controlSx}>
                <CloseIcon fontSize="small" />
              </IconButton>

              <Box
                aria-live="polite"
                component={motion.div}
                key={index}
                initial={reduceMotion ? false : { opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={springSoft}
                sx={{
                  direction: "ltr",
                  unicodeBidi: "isolate",
                  fontFamily,
                  fontSize: { xs: 13, md: 14 },
                  fontWeight: 700,
                  letterSpacing: "0.02em",
                  px: 1.4,
                  py: 0.55,
                  borderRadius: 999,
                  bgcolor: theme.counterBg,
                  boxShadow: `inset 0 0 0 1px ${theme.counterRing}`,
                }}
              >
                {counter}
              </Box>
            </Box>

            {count > 1 && (
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 2,
                  bgcolor: theme.progressTrack,
                  overflow: "hidden",
                }}
              >
                <Box
                  component={motion.div}
                  initial={false}
                  animate={{ scaleX: progress }}
                  transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 260, damping: 28 }}
                  sx={{
                    height: "100%",
                    width: "100%",
                    bgcolor: accent,
                    transformOrigin: "left center",
                  }}
                />
              </Box>
            )}
          </Box>

          <Box
            component={motion.div}
            initial={reduceMotion ? false : { opacity: 0, scale: 0.992 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ ...chromeTransition, delay: reduceMotion ? 0 : 0.04 }}
            sx={{ position: "absolute", inset: 0 }}
          >
            {canNext ? (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: { xs: 6, md: 18 },
                  transform: "translateY(-50%)",
                  zIndex: 3,
                  display: { xs: "none", sm: "block" },
                }}
              >
                <IconButton
                  aria-label="بعدی"
                  onClick={goNext}
                  sx={{ ...controlSx, width: { sm: 44, md: 50 }, height: { sm: 44, md: 50 } }}
                >
                  <ChevronLeftIcon sx={{ fontSize: { sm: 26, md: 30 } }} />
                </IconButton>
              </Box>
            ) : null}
            {canPrev ? (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: { xs: 6, md: 18 },
                  transform: "translateY(-50%)",
                  zIndex: 3,
                  display: { xs: "none", sm: "block" },
                }}
              >
                <IconButton
                  aria-label="قبلی"
                  onClick={goPrev}
                  sx={{ ...controlSx, width: { sm: 44, md: 50 }, height: { sm: 44, md: 50 } }}
                >
                  <ChevronRightIcon sx={{ fontSize: { sm: 26, md: 30 } }} />
                </IconButton>
              </Box>
            ) : null}

            <Box
              ref={emblaRef}
              onPointerDown={onDismissPointerDown}
              onPointerMove={onDismissPointerMove}
              onPointerUp={onDismissPointerUp}
              onPointerCancel={onDismissPointerUp}
              onClick={(event) => {
                if (event.target === event.currentTarget) closeFromEmpty();
              }}
              sx={{
                height: "100%",
                overflow: "hidden",
                touchAction: "none",
              }}
            >
              <Box
                onClick={(event) => {
                  if (event.target === event.currentTarget) closeFromEmpty();
                }}
                sx={{
                  display: "flex",
                  height: "100%",
                  touchAction: "none",
                }}
              >
                {slides.map((item, itemIndex) => {
                  const active = itemIndex === index;
                  const load = loadedSet.has(itemIndex);
                  return (
                    <Box
                      key={`${item.src}-${itemIndex}`}
                      onClick={(event) => {
                        if (event.target === event.currentTarget) closeFromEmpty();
                      }}
                      sx={{
                        flex: "0 0 100%",
                        minWidth: 0,
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: { xs: 2, sm: 7, md: 11 },
                        pt: { xs: 7, md: 8 },
                        pb: { xs: count > 1 ? 14 : 7, md: count > 1 ? 16 : 8 },
                        cursor: "default",
                      }}
                    >
                      {item.type === "image" ? (
                        failedSrc === item.src ? (
                          <Typography sx={{ fontFamily, color: theme.muted }}>تصویر پیدا نشد</Typography>
                        ) : (
                          <GalleryImage
                            src={item.src}
                            thumb={item.thumb}
                            alt={item.alt || `تصویر ${(itemIndex + 1).toLocaleString("fa-IR")}`}
                            active={active}
                            load={load}
                            reduceMotion={reduceMotion}
                            spinnerColor={accent}
                            onError={() => setFailedSrc(item.src)}
                          />
                        )
                      ) : load ? (
                        <Box
                          component={motion.video}
                          src={active ? item.src : undefined}
                          poster={item.thumb}
                          controls
                          playsInline
                          autoPlay={active}
                          onClick={(event: React.MouseEvent) => event.stopPropagation()}
                          initial={reduceMotion ? false : { opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.24, ease: easeOut }}
                          sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            width: "min(100%, 920px)",
                            borderRadius: 1,
                            bgcolor: "#000",
                          }}
                        >
                          مرورگر شما پخش ویدئو را پشتیبانی نمی‌کند.
                        </Box>
                      ) : (
                        <Box sx={{ position: "relative", width: "60%", aspectRatio: "1", maxHeight: "70%" }}>
                          <Image
                            src={item.thumb}
                            alt=""
                            fill
                            sizes="40vw"
                            style={{ objectFit: "contain", opacity: 0.5 }}
                          />
                          <PlayCircleIcon
                            sx={{ position: "absolute", inset: 0, m: "auto", fontSize: 48, color: theme.fg }}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Box
            component={motion.div}
            initial={reduceMotion ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={chromeTransition}
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 4,
              pb: "env(safe-area-inset-bottom)",
              background: `linear-gradient(to top, ${theme.bg} 0%, ${theme.chrome} 72%, transparent)`,
            }}
          >
            <AnimatePresence mode="wait">
              {isImage && slide.alt ? (
                <Typography
                  key={slide.alt}
                  component={motion.p}
                  initial={reduceMotion ? false : { opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduceMotion ? undefined : { opacity: 0 }}
                  transition={{ duration: 0.18, ease: easeOut }}
                  sx={{
                    px: { xs: 2, md: 4 },
                    pt: 1,
                    pb: 0.25,
                    m: 0,
                    textAlign: "center",
                    fontFamily,
                    fontSize: { xs: 12, md: 13 },
                    lineHeight: 1.5,
                    color: theme.muted,
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {slide.alt}
                </Typography>
              ) : null}
            </AnimatePresence>

            {count > 1 && (
              <Box
                sx={{
                  px: { xs: 1.25, md: 3 },
                  pb: { xs: 1.25, md: 1.75 },
                  pt: 1,
                  bgcolor: theme.dock,
                }}
              >
                <Box ref={thumbsRef} sx={{ overflow: "hidden" }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: { xs: 1, md: 1.25 },
                      ...(centerThumbs ? { justifyContent: "center" } : null),
                    }}
                  >
                    {slides.map((item, itemIndex) => {
                      const selected = itemIndex === index;
                      return (
                        <Box
                          key={`thumb-${item.src}-${itemIndex}`}
                          component="button"
                          type="button"
                          aria-label={
                            item.type === "video"
                              ? "پخش ویدئو"
                              : `تصویر ${(itemIndex + 1).toLocaleString("fa-IR")}`
                          }
                          aria-current={selected ? "true" : undefined}
                          onClick={() => onIndexChange(itemIndex)}
                          sx={{
                            flex: "0 0 auto",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "stretch",
                            gap: 0.6,
                            width: { xs: 54, md: 70 },
                            p: 0,
                            border: 0,
                            bgcolor: "transparent",
                            cursor: "pointer",
                            outline: "none",
                            "&:hover .pg-thumb-frame": { opacity: 1 },
                            "&:focus-visible .pg-thumb-frame": {
                              boxShadow: `0 0 0 2px ${theme.bg}, 0 0 0 4px ${accent}`,
                            },
                          }}
                        >
                          <Box
                            className="pg-thumb-frame"
                            sx={{
                              position: "relative",
                              width: "100%",
                              aspectRatio: "1",
                              borderRadius: 1.25,
                              overflow: "hidden",
                              bgcolor: theme.thumbBg,
                              opacity: selected ? 1 : theme.thumbIdle,
                              boxShadow: `inset 0 0 0 1px ${theme.thumbRing}`,
                              transition: "opacity 180ms ease",
                            }}
                          >
                            <Image
                              src={item.thumb}
                              alt=""
                              fill
                              sizes="70px"
                              quality={55}
                              {...(itemIndex <= 3
                                ? { priority: true as const }
                                : { loading: "lazy" as const })}
                              draggable={false}
                              style={{ objectFit: "cover" }}
                            />
                            {item.type === "video" && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  inset: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  bgcolor: "rgba(0,0,0,0.28)",
                                  color: "#fff",
                                }}
                              >
                                <PlayCircleIcon sx={{ fontSize: 26 }} />
                              </Box>
                            )}
                          </Box>
                          <Box
                            component={motion.div}
                            initial={false}
                            animate={{
                              scaleX: selected ? 1 : 0,
                              opacity: selected ? 1 : 0,
                            }}
                            transition={springSoft}
                            sx={{
                              height: 2.5,
                              borderRadius: 99,
                              bgcolor: accent,
                              transformOrigin: "center",
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
